/**
 * DIE KENNUNG EINER GUIDE-KACHEL — eine Definition fuer BEIDE Seiten.
 *
 * WARUM DIESE DATEI AUSSERHALB VON src/ UND server/ LIEGT
 * -------------------------------------------------------
 * Die Kennung wurde an zwei Stellen gebildet: im Browser beim Herrichten des
 * Fensters (GuidePanel: layoutAusRezept) und im Server beim Pruefen des Rezepts
 * (guide.ts). Laufen die auseinander, prueft das Tor ein anderes Fenster als das,
 * das Rafael sieht — und niemand merkt es, weil beide Seiten fuer sich stimmig sind.
 *
 * DER FEHLER, DEN SIE BEHEBT (Rafael, 06.09. 16:23Z, mit Bild)
 * ------------------------------------------------------------
 * Er beantwortete k16-projektakte-uebergabe um 16:22:11Z, der Guide sprang auf die
 * naechste Karte desselben Themas (16:22:17Z), und das Layout wurde um 16:23:13Z
 * korrekt neu geschrieben — mit der richtigen neuen Datei in der config. Sein Bild
 * von 16:23:28Z zeigte links die neue Karte und rechts weiterhin die Seite der
 * VORIGEN. Herrichten hatte funktioniert; die Kachel hatte den Wechsel nicht
 * mitgemacht.
 *
 * Ursache: die Kennung war `#guide-p-<component>-<i>` — fuer jede Karte desselben
 * Themas dieselbe. Aus Sicht der Layout-Engine war es derselbe Knoten, der einfach
 * stehenbleiben konnte; die geaenderte config kam am Panel nie an.
 *
 * DIE LOESUNG: DER INHALT GEHOERT IN DIE KENNUNG.
 * Zeigt die Kachel etwas anderes, ist sie ein anderer Knoten und wird neu gebaut.
 * Zeigt sie dasselbe, bleibt die Kennung gleich und nichts flackert grundlos.
 */

/**
 * Kurzer, stabiler Abdruck eines Kachel-Inhalts. Bewusst kein Krypto-Hash: das hier
 * unterscheidet Kacheln, es schuetzt nichts. Wichtig ist allein, dass beide Seiten
 * bei gleicher Eingabe dieselben Zeichen herausbekommen — deshalb werden die
 * Schluessel sortiert, bevor gerechnet wird (JSON.stringify haelt sonst die
 * Einfuegereihenfolge fest, und die kann sich je nach Weg unterscheiden).
 */
export function inhaltsAbdruck(inhalt: unknown): string {
  const text = stabilerText(inhalt);
  // djb2 — klein, deterministisch, in beiden Laufzeiten identisch.
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36).padStart(7, '0').slice(0, 7);
}

function stabilerText(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return `[${v.map(stabilerText).join(',')}]`;
  const paare = Object.keys(v as Record<string, unknown>).sort()
    .map(k => `${JSON.stringify(k)}:${stabilerText((v as Record<string, unknown>)[k])}`);
  return `{${paare.join(',')}}`;
}

/**
 * Die Kennung einer Panel-Kachel. `i` bleibt drin, damit zwei Kacheln mit demselben
 * Inhalt nebeneinander stehen koennen, ohne zu kollidieren.
 */
export function panelKachelId(component: string, i: number, inhalt: unknown): string {
  return `#guide-p-${component}-${i}-${inhaltsAbdruck(inhalt ?? {})}`;
}

/**
 * Die Kennung einer Sitzungs-Kachel. Sie traegt die Sitzung schon im Namen, also
 * unterscheidet sie sich beim Wechsel ohnehin — hier reicht die bisherige Form.
 */
export function sitzungsKachelId(sessionId: string, i: number): string {
  return `#guide-s-${sessionId.slice(0, 8)}-${i}`;
}
